import * as React from "react"
import { useCallback, useEffect, useId, useRef, useState } from "react"
import { cn } from "../../lib/cn"
import { useDismiss } from "../../hooks/useDismiss"
import { useRovingActiveIndex } from "../../hooks/useRovingActiveIndex"

export interface ListboxOption<T> {
    id: string
    value: T
    disabled?: boolean
}

export interface ListboxSelectProps<T> {
    options: ListboxOption<T>[]
    /** id of the currently-selected option (drives aria-selected + initial active). */
    selectedId?: string | undefined
    onSelect: (value: T, option: ListboxOption<T>) => void
    renderOption: (option: ListboxOption<T>, state: { active: boolean; selected: boolean }) => React.ReactNode
    /** Render-prop for the trigger; spread `triggerProps` and attach `ref` to a <button>. */
    trigger: (props: {
        open: boolean
        ref: React.Ref<HTMLButtonElement>
        triggerProps: React.ButtonHTMLAttributes<HTMLButtonElement>
    }) => React.ReactNode
    /** Accessible name for the listbox. */
    label: string
    align?: "start" | "end"
    /** Extra classes for the floating panel (e.g. width). */
    panelClassName?: string
    emptyState?: React.ReactNode
    /** Controlled open state (omit for internal/uncontrolled). */
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

/**
 * Accessible single-select listbox opened by a button. Focus moves into the
 * listbox on open and is tracked virtually via `aria-activedescendant`
 * (Arrow/Home/End move the active option, Enter/Space select, Esc/Tab/outside-click
 * close). Composes the shared `useDismiss` + `useRovingActiveIndex` core. Supports
 * controlled `open`/`onOpenChange` for callers that own the open state (e.g. a hook).
 */
export function ListboxSelect<T>({
    options,
    selectedId,
    onSelect,
    renderOption,
    trigger,
    label,
    align = "end",
    panelClassName,
    emptyState,
    open: controlledOpen,
    onOpenChange,
}: ListboxSelectProps<T>) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const open = controlledOpen ?? uncontrolledOpen
    const setOpen = useCallback((next: boolean) => {
        if (controlledOpen === undefined) setUncontrolledOpen(next)
        onOpenChange?.(next)
    }, [controlledOpen, onOpenChange])

    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLUListElement>(null)
    const baseId = useId()
    const listboxId = `${baseId}-listbox`
    const optionId = (i: number) => `${baseId}-option-${i}`

    useDismiss({ open, onClose: () => setOpen(false), triggerRef, panelRef })

    const selectByIndex = useCallback((index: number) => {
        const opt = options[index]
        if (!opt || opt.disabled) return
        onSelect(opt.value, opt)
        setOpen(false)
        triggerRef.current?.focus()
    }, [options, onSelect, setOpen])

    const { activeIndex, setActiveIndex, onKeyDown: rovingKeyDown } = useRovingActiveIndex({
        itemCount: options.length,
        wrap: "clamp",
        listRef: panelRef,
        onSelect: selectByIndex,
    })

    // On open: preset the active option to the selected one (or first) and move
    // focus into the listbox so the keyboard handler + aria-activedescendant apply.
    useEffect(() => {
        if (!open) return
        const selIdx = selectedId ? options.findIndex(o => o.id === selectedId) : -1
        setActiveIndex(selIdx >= 0 ? selIdx : 0)
        panelRef.current?.focus()
        // Intentionally only on the open transition.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const triggerProps: React.ButtonHTMLAttributes<HTMLButtonElement> = {
        "aria-haspopup": "listbox",
        "aria-expanded": open,
        onClick: () => setOpen(!open),
        onKeyDown: (e) => {
            if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ")) {
                e.preventDefault()
                setOpen(true)
            }
        },
    }

    const onListKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Tab") { setOpen(false); return }
        rovingKeyDown(e)
    }

    return (
        <div className="relative">
            {trigger({ open, ref: triggerRef, triggerProps })}
            {open && (
                <ul
                    ref={panelRef}
                    id={listboxId}
                    role="listbox"
                    aria-label={label}
                    tabIndex={-1}
                    aria-activedescendant={activeIndex >= 0 && options.length > 0 ? optionId(activeIndex) : undefined}
                    onKeyDown={onListKeyDown}
                    className={cn(
                        "absolute top-full mt-1 z-50 overflow-hidden rounded-xl border border-border-default bg-surface-base shadow-dropdown focus:outline-none",
                        align === "end" ? "right-0" : "left-0",
                        panelClassName,
                    )}
                >
                    {options.length === 0 && emptyState ? (
                        <li role="presentation">{emptyState}</li>
                    ) : (
                        options.map((opt, i) => (
                            <li
                                key={opt.id}
                                id={optionId(i)}
                                role="option"
                                aria-selected={opt.id === selectedId}
                                aria-disabled={opt.disabled || undefined}
                                onClick={() => selectByIndex(i)}
                                onMouseMove={() => setActiveIndex(i)}
                                className="cursor-pointer"
                            >
                                {renderOption(opt, { active: i === activeIndex, selected: opt.id === selectedId })}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    )
}
